import type { UserSettings } from '@zinc/shared';
import { useEffect } from 'react';
import { Link } from 'react-router';
import { useSaveSettings } from '../../lib/queries';
import { SolvingSettings, type SetSetting } from '../SettingsParts';

/** The puzzle's settings (the cog by undo and redo): each person's solving settings, changed in
 * place, in the same groups as the Settings page (Checking, Highlights, Pad, Other). */
export function SettingsSheet({
  settings,
  onClose,
}: {
  settings: UserSettings;
  onClose: () => void;
}) {
  const save = useSaveSettings();
  const set: SetSetting = (key) => (value) => save.mutate({ [key]: value });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(31,30,28,0.35)] p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-sheet-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[calc(100dvh-24px)] w-full max-w-[420px] flex-col gap-3 overflow-y-auto rounded-[20px] border border-line bg-bg px-4 py-4">
        <div className="flex items-center gap-2">
          <h2 id="settings-sheet-title" className="m-0 flex-1 px-1 text-[20px] font-extrabold">
            Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-btn border border-line bg-surface px-4 text-[14px] font-semibold text-ink"
          >
            Done
          </button>
        </div>
        <SolvingSettings s={settings} set={set} />
        {save.isError && (
          <p role="alert" className="mx-1 my-0 text-[13px] text-mistake">
            That change didn’t save. Check your connection and try again.
          </p>
        )}
        <Link to="/settings" className="mx-1 text-[14px] font-semibold">
          All settings
        </Link>
      </div>
    </div>
  );
}
